import { ReactElement } from "react";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { LocalizedStringRecord } from "./../../app/slices/TrainingLocalizationSlice";
import { ValidationError } from "../../app/slices/ValidationSlice";
import { Actor } from "../../models/Actor";
import Interaction from "../../models/interactions/Interaction";
import { IAnswer } from "../../models/Quiz";
import Take from "../../models/Take";
import { Prop } from "../../models/Prop";
import { SearchLookupContext } from "../../models/interactions/SearchableInteractionValues";
import { InteractionProperty } from "../../models/InteractionProperty";

export type InteractionPropertiesEditorActions = {
  updateInteraction: (interaction: Interaction) => void;
  updateProperty: (property: InteractionProperty) => void;
};

export type InteractionPropertiesEditor = {
  handledPropertyIds?: readonly string[];
  render: (
    interaction: Interaction,
    actions: InteractionPropertiesEditorActions
  ) => ReactElement | null;
};

export type ReconcileChange = {
  scope: "preset" | "interaction";
  id: string;
  label: string;
  field: "prefabName";
  oldValue: string;
  newValue: string;
};

export type ReconcileContext = {
  // preset instance id -> fresh prefabName; entries with unknown ids are simply absent
  prefabNameByInstanceId: Record<string, string>;
  // mutable collector appended to by Reconcile implementations
  changes: ReconcileChange[];
};

export type InteractionFactoryEntity = {
  View: ReactElement;
  NodeView?: (nodeId: string, interaction: Interaction) => ReactElement;
  PropertiesEditor?: InteractionPropertiesEditor;
  FilterType: string;
  Create: (getKeys: (amount: number) => string[]) => Interaction;
  Migrate: (from: number, to: number, interaction: Interaction) => Interaction;
  // Pure: must return the SAME object when nothing changed. Container types
  // (Sequence, feedback quizzes) recurse into their nested interactions
  // themselves, like Migrate/Copy do.
  Reconcile?: (
    interaction: Interaction,
    context: ReconcileContext
  ) => Interaction;
  Copy?: (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void,
    getAnswerArray: (originalAnswers: IAnswer[], newKeys: string[]) => IAnswer[]
  ) => Interaction;
  Validate?: (
    interaction: Interaction,
    getValue: (key: string) => string,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets?: Prop[]
  ) => string[];
  ValidateErrors?: (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord
  ) => (ValidationError | string)[];
  Filter?: (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[],
    getValue: (key: string) => string,
    searchLookupContext?: SearchLookupContext
  ) => SearchResultData[];
};

type InteractionMap = {
  [key: string]: InteractionFactoryEntity;
};

const interactionMap: InteractionMap = {};

// Function to register a new interaction
export const registerInteraction = (
  interactionType: string,
  interaction: InteractionFactoryEntity
): void => {
  interactionMap[interactionType] = interaction;
};

// Factory function to retrieve an interaction
export const getInteractionFromFactory = (
  interactionType: string
): InteractionFactoryEntity => {
  return interactionMap[interactionType];
};
